import { useEffect, useState } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";

import { Printer } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { fmtMoney, fmtM2Globale } from "./crmShared";
import "./lotDetails.css";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—");

/** Aperçu A4 d'un devis enregistré + impression tracée (POST /quotes/{id}/print). N'existe qu'APRÈS validation de la fiche client. */
export default function QuotePrintDialog({ quoteId, onClose, onPrinted }) {
  const [q, setQ] = useState(null);
  const [company, setCompany] = useState({});
  const [brand, setBrand] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/crm/quotes/${quoteId}`).then(({ data }) => setQ(data)).catch(() => { toast.error("Devis introuvable"); onClose(); });
    api.get("/settings/company").then(({ data }) => setCompany(data || {})).catch(() => {});
    api.get("/settings/brand").then(({ data }) => setBrand(data || {})).catch(() => {});
  }, [quoteId, onClose]);
  const print = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/quotes/${quoteId}/print`);
      setQ(data); onPrinted?.(data);
      setTimeout(() => { window.print(); setBusy(false); }, 150);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impression impossible"); setBusy(false); }
  };
  if (!q) return null;
  const c = q.client || {};
  return (
    <div className="lot-print-root fixed inset-0 flex items-center justify-center" style={{ zIndex: 1300, background: "rgba(0,0,0,.65)" }} onClick={onClose} data-testid="quote-print-dialog">
      <div className="flex flex-col" onClick={(e) => e.stopPropagation()} style={{ width: 760, maxWidth: "95vw", maxHeight: "92vh", background: "#0e0e10", border: "0.5px solid #2a2a30", borderRadius: 14, overflow: "hidden" }}>
        <div className="no-print flex items-center" style={{ gap: 10, padding: "10px 16px", borderBottom: "0.5px solid #232328" }}>
          <span style={{ fontSize: 13, color: "#e8e8ec", fontWeight: 500 }}>Devis {q.numero}</span>
          <span style={{ fontSize: 10.5, color: "#8a8a8f" }} data-testid="quote-print-count">{q.print_count ? `${q.print_count} impression${q.print_count > 1 ? "s" : ""}` : "jamais imprimé"}</span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={print} disabled={busy} className="inline-flex items-center disabled:opacity-50" style={{ gap: 6, background: "#378ADD", color: "#fff", fontSize: 12, borderRadius: 8, padding: "7px 14px" }} data-testid="quote-print-btn"><Printer size={13} />Imprimer</button>
          <DeskCloseX onClick={onClose} testId="quote-print-close" />
        </div>
        <div className="overflow-y-auto" style={{ padding: 28, background: "#fff", color: "#111", fontFamily: "Tahoma, sans-serif" }} data-testid="quote-print-sheet">
          <div className="flex items-start" style={{ gap: 16, marginBottom: 22 }}>
            {(brand.logo_light || brand.logo_dark) && <img src={brand.logo_light || brand.logo_dark} alt="" style={{ height: 52, objectFit: "contain" }} />}
            <div style={{ flex: 1, fontSize: 11, lineHeight: 1.5 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{company.raison_sociale || "—"}</div>
              <div>{[company.adresse_siege, company.commune, company.wilaya].filter(Boolean).join(", ")}</div>
              <div>{[company.telephone_fixe, company.mobile_1, company.email].filter(Boolean).join(" · ")}</div>
              <div>{[company.rc && `RC ${company.rc}`, company.nif && `NIF ${company.nif}`, company.nis && `NIS ${company.nis}`].filter(Boolean).join(" · ")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#378ADD", letterSpacing: ".04em" }}>DEVIS</div>
              <div style={{ fontSize: 13, fontWeight: 600 }} data-testid="quote-print-numero">{q.numero}</div>
              <div style={{ fontSize: 11, color: "#444" }}>{fmtDate(q.created_at)}</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18, fontSize: 11.5 }}>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 9.5, letterSpacing: ".08em", color: "#666", marginBottom: 6 }}>CLIENT</div>
              <div style={{ fontWeight: 600 }}>{[c.civilite, c.nom, c.prenom].filter(Boolean).join(" ")}</div>
              {c.telephone && <div>{c.telephone}</div>}{c.email && <div>{c.email}</div>}{c.profession && <div style={{ color: "#555" }}>{c.profession}</div>}
            </div>
            <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 9.5, letterSpacing: ".08em", color: "#666", marginBottom: 6 }}>PROJET</div>
              <div style={{ fontWeight: 600 }}>{q.project_name || "—"}</div>
              <div>{q.lots.length} lot{q.lots.length > 1 ? "s" : ""} · Commercial : {q.created_by_name}</div>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }} data-testid="quote-print-lots">
            <thead><tr style={{ background: "#f3f3f5" }}>{["Lot", "Type", "Bloc / Niveau", "Surface", "TVA", "Prix TTC"].map((h) => <th key={h} style={{ textAlign: h === "Prix TTC" || h === "Surface" ? "right" : "left", padding: "7px 8px", borderBottom: "1px solid #ccc", fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {q.lots.map((l) => <tr key={l.id} style={{ borderBottom: "1px solid #e5e5e5" }}>
                <td style={{ padding: "7px 8px", fontWeight: 600 }}>{l.numero}</td><td style={{ padding: "7px 8px" }}>{l.type}{l.label ? ` ${l.label}` : ""}</td>
                <td style={{ padding: "7px 8px" }}>{[l.block_name && `Bloc ${l.block_name}`, l.level_label].filter(Boolean).join(" · ")}</td>
                <td style={{ padding: "7px 8px", textAlign: "right" }}>{fmtM2Globale(l.surface_globale) || (l.surface ? `${l.surface} m²` : "—")}</td><td style={{ padding: "7px 8px" }}>{l.taux_tva != null ? `${l.taux_tva} %` : "—"}</td>
                <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600 }}>{fmtMoney(l.price_ttc)}</td>
              </tr>)}
            </tbody>
            <tfoot><tr><td colSpan={5} style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700 }}>TOTAL TTC</td><td style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, fontSize: 13, color: "#1f8f68" }} data-testid="quote-print-total">{fmtMoney(q.total_ttc)}</td></tr></tfoot>
          </table>
          {q.appreciation && <div style={{ marginTop: 14, fontSize: 11, color: "#444" }}><b>Remarques :</b> {q.appreciation}</div>}
          <div style={{ marginTop: 26, fontSize: 10, color: "#777" }}>Devis sans effet sur la disponibilité des lots · valable sous réserve de disponibilité au moment de la réservation.</div>
        </div>
      </div>
    </div>
  );
}
